import React, { useEffect, useState } from "react";
import { api, asList, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { format } from "date-fns";
import { Loader2, Check } from "lucide-react";

/** Book a new appointment for an incoming visitor. */
export default function AppointmentDialog({ open, onClose, onDone }) {
  const [f, setF] = useState({
    visitor_name: "",
    visitor_phone: "",
    visitor_company: "",
    host_user_id: "",
    scheduled_at: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    purpose: "",
  });
  const [hosts, setHosts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return; setError("");
    (async () => {
      try { const { data } = await api.get("/users"); setHosts(asList(data)); }
      catch (err) { void err; }
    })();
  }, [open]);

  const submit = async () => {
    if (!f.visitor_name.trim() || !f.host_user_id) { setError("Visitor name and host required"); return; }
    setSaving(true);
    try {
      await api.post("/visitors/appointments", { ...f, scheduled_at: new Date(f.scheduled_at).toISOString() });
      toast.success("Appointment created");
      onDone();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="visitor-appt-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">New Appointment</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Visitor</Label>
            <Input value={f.visitor_name} onChange={(e) => setF({ ...f, visitor_name: e.target.value })} data-testid="appt-name"/>
          </div>
          <div><Label>Phone</Label><Input value={f.visitor_phone} onChange={(e) => setF({ ...f, visitor_phone: e.target.value })}/></div>
          <div><Label>Company</Label><Input value={f.visitor_company} onChange={(e) => setF({ ...f, visitor_company: e.target.value })}/></div>
          <div className="col-span-2"><Label>Host</Label>
            <Select value={f.host_user_id || undefined} onValueChange={(v) => setF({ ...f, host_user_id: v })}>
              <SelectTrigger data-testid="appt-host"><SelectValue placeholder="Pick host"/></SelectTrigger>
              <SelectContent className="max-h-64">
                {hosts.map((h) => <SelectItem key={h.id} value={h.id}>{h.name} · {h.role}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Scheduled at</Label>
            <Input
              type="datetime-local"
              value={f.scheduled_at}
              onChange={(e) => setF({ ...f, scheduled_at: e.target.value })}
            />
          </div>
          <div className="col-span-2"><Label>Purpose</Label>
            <Textarea rows={2} value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })}/>
          </div>
        </div>
        {error && <div className="text-sm text-red-600">{error}</div>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-[#0F5E39] hover:bg-[#083F22] text-white"
            data-testid="appt-submit-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin mr-2"/> : <Check size={14} className="mr-1"/>}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
